/**
 * GalleryContentSection.tsx - Gallery Content Filtering Configuration
 *
 * This component provides configuration options for controlling which
 * fundraisers appear in the public gallery with filtering and selection options.
 *
 * Key Features:
 * - Fundraiser display filtering (all, active, specific)
 * - Timeframe-based filtering for recent fundraisers
 * - Manual fundraiser selection for curated galleries
 * - Status-based filtering and organization
 * - Preview of filtered fundraiser results
 * - Bulk selection and management tools
 *
 * Content Filtering Options:
 * - All Fundraisers: Display all available fundraisers
 * - Active Only: Show only currently active fundraisers
 * - Recent: Filter by creation or activity date
 * - Specific: Manually curated fundraiser selection
 *
 * Timeframe Filtering:
 * - Last 30 days: Recently created or active fundraisers
 * - Last 90 days: Quarterly fundraiser activity
 * - Custom date range: User-defined time periods
 * - Activity-based: Filter by last donation or update
 *
 * Manual Selection:
 * - Fundraiser search and selection interface
 * - Drag-and-drop ordering for display priority
 * - Bulk selection and deselection tools
 * - Preview of selected fundraisers
 *
 * Props: None (uses MobX store for gallery settings and fundraiser data)
 *
 * Dependencies: FundraiserSelector, date filtering, MobX store
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Filter, Plus, X } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { FundraiserSelector } from "../shared/FundraiserSelector";
import { GalleryDisplayFundraisersType } from "../../enums";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const Select = styled.select`
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;
  width: 400px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DropdownWithOptions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const SubFieldContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px; /* Increased from 8px to 12px for more consistent spacing */
`;

const SubFieldLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  min-width: fit-content; /* Ensure labels don't compress */
`;

const SubFieldInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  width: 80px;
  text-align: center;
  transition: border-color 0.2s ease;

  /* Remove number input spinners */
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Input = styled.input`
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const AddFundraiserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  color: #3b82f6;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover {
    background: #f0f9ff;
    color: #2563eb;
  }
`;

const SelectedFundraisersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const SelectedFundraiserBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #0ea5e9;
`;

const BadgeRemoveButton = styled.button`
  background: none;
  border: none;
  color: #0369a1;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 16px;
  height: 16px;

  &:hover {
    background: rgba(3, 105, 161, 0.1);
  }
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.4;
`;

interface GalleryContentSectionProps {
  onSettingsChange: () => void;
}

export const GalleryContentSection: React.FC<GalleryContentSectionProps> =
  observer(({ onSettingsChange }) => {
    const { gallerySettings } = fundraiserStore;
    const [showFundraiserSelector, setShowFundraiserSelector] = useState(false);

    const handleDisplayFundraisersChange = (
      value: keyof typeof GalleryDisplayFundraisersType
    ) => {
      fundraiserStore.updateGallerySettings("displayFundraisers", value);

      // Clear selected fundraisers when changing display option
      fundraiserStore.updateGallerySettings("specificFundraiserIds", []);

      onSettingsChange();

      // Set default values for specific options
      if (value === "AllActiveStarting" && !gallerySettings.timeframeDays) {
        fundraiserStore.updateGallerySettings("timeframeDays", "3");
      } else if (value === "RecentlyAdded" && !gallerySettings.timeframeDays) {
        fundraiserStore.updateGallerySettings("timeframeDays", "90");
      }

      // Auto-open selector if "Specific" is selected but no fundraisers are selected
      if (
        value === "Specific" &&
        (!gallerySettings.specificFundraiserIds ||
          gallerySettings.specificFundraiserIds.length === 0)
      ) {
        setShowFundraiserSelector(true);
      }
    };

    const handleTimeframeDaysChange = (value: string) => {
      fundraiserStore.updateGallerySettings("timeframeDays", value);
      onSettingsChange();
    };

    const handleFundraiserSelectionChange = (fundraiserIds: string[]) => {
      fundraiserStore.updateGallerySettings(
        "specificFundraiserIds",
        fundraiserIds
      );
      onSettingsChange();
    };

    const removeFundraiserFromSelection = (fundraiserId: string) => {
      const current = gallerySettings.specificFundraiserIds || [];
      const updated = current.filter((id) => id !== fundraiserId);
      fundraiserStore.updateGallerySettings("specificFundraiserIds", updated);
      onSettingsChange();
    };

    const getSelectedFundraisers = () => {
      return (gallerySettings.specificFundraiserIds || [])
        .map((id) => fundraiserStore.fundraisers.find((f) => f.id === id))
        .filter(Boolean);
    };

    return (
      <Section>
        <SectionTitle>
          <Filter size={24} />
          Content
        </SectionTitle>

        <FormGroup>
          <Label htmlFor="displayFundraisers">Display Fundraisers</Label>
          <DropdownWithOptions>
            <Select
              id="displayFundraisers"
              value={gallerySettings.displayFundraisers}
              onChange={(e) =>
                handleDisplayFundraisersChange(
                  e.target.value as keyof typeof GalleryDisplayFundraisersType
                )
              }
            >
              <option value={GalleryDisplayFundraisersType.AllActive}>
                All Active
              </option>
              <option value={GalleryDisplayFundraisersType.AllActiveStarted}>
                All Active and Started
              </option>
              <option value={GalleryDisplayFundraisersType.AllActiveStarting}>
                All Active and Starting
              </option>
              <option value={GalleryDisplayFundraisersType.RecentlyAdded}>
                Recently Added
              </option>
              <option value={GalleryDisplayFundraisersType.Specific}>
                Specific
              </option>
            </Select>

            {/* Show within days input for All Active and Starting */}
            {gallerySettings.displayFundraisers === "AllActiveStarting" && (
              <SubFieldContainer>
                <SubFieldLabel>within the next</SubFieldLabel>
                <SubFieldInput
                  type="number"
                  min="1"
                  max="30"
                  style={
                    gallerySettings.hasError("timeframeDays")
                      ? { borderColor: "red" }
                      : {}
                  }
                  value={gallerySettings.timeframeDays}
                  onChange={(e) => handleTimeframeDaysChange(e.target.value)}
                />
                <SubFieldLabel>days</SubFieldLabel>
              </SubFieldContainer>
            )}

            {/* Show days ago input for Recently Added */}
            {gallerySettings.displayFundraisers === "RecentlyAdded" && (
              <SubFieldContainer>
                <SubFieldLabel>within the past</SubFieldLabel>
                <SubFieldInput
                  type="number"
                  min="1"
                  max="365"
                  style={
                    gallerySettings.hasError("timeframeDays")
                      ? { borderColor: "red" }
                      : {}
                  }
                  value={gallerySettings.timeframeDays}
                  onChange={(e) => handleTimeframeDaysChange(e.target.value)}
                />
                <SubFieldLabel>days</SubFieldLabel>
              </SubFieldContainer>
            )}
          </DropdownWithOptions>

          {/* Show specific fundraiser selection */}
          {gallerySettings.displayFundraisers === "Specific" && (
            <div style={{ marginTop: "16px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "8px",
                }}
              >
                <Label>Selected Fundraisers</Label>
                <AddFundraiserButton
                  onClick={() => setShowFundraiserSelector(true)}
                >
                  <Plus size={16} />
                  Add Fundraisers
                </AddFundraiserButton>
              </div>

              {getSelectedFundraisers().length > 0 ? (
                <SelectedFundraisersContainer>
                  {getSelectedFundraisers().map((fundraiser: any) => (
                    <SelectedFundraiserBadge key={fundraiser.id}>
                      {fundraiser.title || fundraiser.name}
                      <BadgeRemoveButton
                        onClick={() =>
                          removeFundraiserFromSelection(fundraiser.id)
                        }
                      >
                        <X size={12} />
                      </BadgeRemoveButton>
                    </SelectedFundraiserBadge>
                  ))}
                </SelectedFundraisersContainer>
              ) : (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: "14px",
                    background: "#f8fafc",
                    border: gallerySettings.hasError("specificFundraiserIds")
                      ? "2px solid red"
                      : "1px solid #e2e8f0",
                    borderRadius: "8px",
                  }}
                >
                  No fundraisers selected. Click "Add Fundraisers" to choose
                  specific fundraisers.
                </div>
              )}
            </div>
          )}
        </FormGroup>

        {/* Fundraiser Selector Modal */}
        <FundraiserSelector
          isModal={true}
          isVisible={showFundraiserSelector}
          onClose={() => setShowFundraiserSelector(false)}
          selectedFundraiserIds={gallerySettings.specificFundraiserIds || []}
          onSelectionChange={handleFundraiserSelectionChange}
          status={["Active", "Completed", "Inactive", "Draft"]}
          availability={["current", "future"]}
          multiple="yes"
        />
      </Section>
    );
  });
